/**
 * @changelog
 *
 *
 * 2026-02-28 (0.1.0) (jhoward)
 *   - Add /offers/:offerDomain/:offerCode/view route for viewing offer details.
 *
 * 2026-03-11 (0.1.1) (jhoward)
 *   - Add 'pageTitle' and 'twoFactorAuthRequired' meta fields to 'offers.view' route.
 *
 * 2026-03-17 (0.2.0) (jhoward)
 *   - Add adminRoutes with dashboard and user management routes.
 *
 * 2026-03-24 (0.3.0) (jhoward)
 *   - Add admin.maintenance-mode and admin.offers.refresh routes.
 *   - Fix pageTitle for username help.
 *
 * 2026-03-30 (0.4.0) (jhoward)
 *   - Add routes for /access-log, /access-log/:id/view, /user-log, and /user-log/:id/view.
 *
 * 2026-04-09 (0.5.0) (jhoward)
 *   - Add /user-lookup and /user-lookup/:id/view admin routes.
 *
 * 2026-09-18 (0.6.0) (jhoward)
 *   - Replace admin.offers.refresh with admin.login-data.update, at /login-data/update. The screen refetches the
 *     whole login payload now, not the offers alone, so neither its path nor its title says offers.
 *
 * 2026-10-09 (0.7.0) (jhoward)
 *   - Add admin.user.two-factor at /admin/user/two-factor.
 *   - Add /admin/faq, /admin/faq/new and /admin/faq/:id/edit for managing FAQ articles.
 *
 * 2026-10-09 (0.8.0) (jhoward)
 *   - Add /admin/assistant, the employee assistant chat.
 *
 * 2026-10-09 (0.9.0) (jhoward)
 *   - Add /admin/chatbot, where staff turn the chatbots on and off per property.
 *
 * 2026-10-10 (0.10.0) (jhoward)
 *   - Add admin.security-audit at /admin/security-audit and admin.settings.security at /admin/settings/security.
 */

import type { RouteRecordRaw } from 'vue-router';

const adminRoutes: RouteRecordRaw[] = [
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/Admin/AdminHome.vue'),
    meta: {
      pageTitle: 'Admin Dashboard',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
  },
  {
    path: '/admin/user/password',
    name: 'admin.user.password',
    component: () => import('@/views/Admin/SendUserPasswordResetLink.vue'),
    meta: {
      pageTitle: 'Send User Password Reset Link',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
  },
  {
    path: '/admin/user/email-or-phone',
    name: 'admin.user.email-or-phone',
    component: () => import('@/views/Admin/ResetUserEmailOrPhone.vue'),
    meta: {
      pageTitle: 'Reset User Email or Phone',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
  },
  {
    path: '/admin/user/username',
    name: 'admin.user.username',
    component: () => import('@/views/Admin/SendUserUsernameHelpLink.vue'),
    meta: {
      pageTitle: 'Send Username Help Link',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
  },
  {
    path: '/admin/user/two-factor',
    name: 'admin.user.two-factor',
    component: () => import('@/views/Admin/ResetUserTwoFactor.vue'),
    meta: {
      pageTitle: 'Reset User Two-Factor',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
  },
  {
    path: '/admin/security-audit',
    name: 'admin.security-audit',
    component: () => import('@/views/Admin/SecurityAudit.vue'),
    meta: {
      pageTitle: 'Security Audit',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
  },
  {
    path: '/admin/settings/security',
    name: 'admin.settings.security',
    component: () => import('@/views/Admin/SecuritySettings.vue'),
    meta: {
      pageTitle: 'Security Settings',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
  },
  {
    path: '/admin/maintenance-mode',
    name: 'admin.maintenance-mode',
    component: () => import('@/views/Admin/MaintenanceMode.vue'),
    meta: {
      pageTitle: 'Maintenance mode',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
  },
  {
    path: '/admin/assistant',
    name: 'admin.assistant',
    component: () => import('@/views/Admin/Assistant.vue'),
    meta: {
      pageTitle: 'Support Assistant',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
  },
  {
    path: '/admin/chatbot',
    name: 'admin.chatbot',
    component: () => import('@/views/Admin/Chatbot.vue'),
    meta: {
      pageTitle: 'Chatbot Switches',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
  },
  {
    path: '/admin/faq',
    name: 'admin.faq',
    component: () => import('@/views/Admin/FaqList.vue'),
    meta: {
      pageTitle: 'FAQ',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
  },
  {
    path: '/admin/faq/new',
    name: 'admin.faq.new',
    component: () => import('@/views/Admin/FaqEdit.vue'),
    meta: {
      pageTitle: 'New FAQ',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
  },
  {
    path: '/admin/faq/:id/edit',
    name: 'admin.faq.edit',
    component: () => import('@/views/Admin/FaqEdit.vue'),
    meta: {
      pageTitle: 'Edit FAQ',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
    props: true,
  },
  {
    path: '/login-data/update',
    name: 'admin.login-data.update',
    component: () => import('@/views/Admin/UpdateLoginData.vue'),
    meta: {
      pageTitle: 'Update Login Data',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
    props: true,
  },

  {
    path: '/access-log',
    name: 'admin.access-log',
    component: () => import('@/views/Admin/AccessLog.vue'),
    meta: {
      pageTitle: 'Access Log',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
    props: true,
  },
  {
    path: '/access-log/:id/view',
    name: 'admin.access-log.view',
    component: () => import('@/views/Admin/AccessLogViewItem.vue'),
    meta: {
      pageTitle: 'View Access Log',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
    props: true,
  },

  {
    path: '/user-log',
    name: 'admin.user-log',
    component: () => import('@/views/Admin/UserLog.vue'),
    meta: {
      pageTitle: 'User Log',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
    props: true,
  },
  {
    path: '/user-log/:id/view',
    name: 'admin.user-log.view',
    component: () => import('@/views/Admin/UserLogViewItem.vue'),
    meta: {
      pageTitle: 'View User Log',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
    props: true,
  },

  {
    path: '/user-lookup',
    name: 'admin.user-lookup',
    component: () => import('@/views/Admin/UserLookup.vue'),
    meta: {
      pageTitle: 'User Lookup',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
    props: true,
  },
  {
    path: '/user-lookup/:id/view',
    name: 'admin.user-lookup.view',
    component: () => import('@/views/Admin/ViewUser.vue'),
    meta: {
      pageTitle: 'View User',
      authRequired: true,
      twoFactorAuthRequired: true,
      adminRequired: true,
    },
    props: true,
  },
];

export default adminRoutes;
